<script>
import InboxesAPI from 'dashboard/api/inboxes';
import MessageCardPreview from 'dashboard/components-next/whatsapp/MessageCardPreview.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import NextSelect from 'dashboard/components-next/select/Select.vue';
import NextTextArea from 'dashboard/components-next/textarea/TextArea.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import AiTemplateDraft from 'dashboard/components-next/whatsapp/AiTemplateDraft.vue';
import { existingTemplateDraft } from 'dashboard/helper/aiTemplateDraft';
import { getWhatsappTemplateErrorMessage } from 'dashboard/helper/templateHelper';
import { useAlert } from 'dashboard/composables';
import {
  insertAtCursor,
  systemParameterExample,
} from 'dashboard/helper/systemParameters';
import SystemParameterMenu from 'dashboard/components-next/systemParameters/SystemParameterMenu.vue';
import TemplateButtonsField from './whatsappTemplate/TemplateButtonsField.vue';
import MessageValidityField from './whatsappTemplate/MessageValidityField.vue';
import {
  COPY_CODE_TEXT,
  emptyTemplateButton,
  hasButtonContent,
  templateButtonToForm,
} from 'dashboard/helper/whatsappTemplateButtons';
import {
  TEMPLATE_TYPES,
  TEMPLATE_CATEGORIES,
  ORDER_DETAILS_BUTTON_MAX_LENGTH,
  buildWhatsappTemplatePayload,
  defaultOrderDetailsButtonText,
  defaultTypeForCategory,
  detectWhatsappTemplateType,
  getTemplateTypeConfig,
  isTypeAllowedInCategory,
  orderDetailsButtonText,
  typesForCategory,
} from 'dashboard/helper/whatsappTemplateTypes';

const FOOTER_MAX_LENGTH = 60;
const HEADER_TEXT_MAX_LENGTH = 60;
const BODY_MAX_LENGTH = 1024;
const normalizeTemplateName = value =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/_+/g, '_');

const emptyButton = () => emptyTemplateButton();

const initialForm = () => ({
  id: null,
  name: '',
  language: 'pt_BR',
  category: 'MARKETING',
  template_type: TEMPLATE_TYPES.STANDARD,
  order_details_button_text: defaultOrderDetailsButtonText('pt_BR'),
  add_security_recommendation: true,
  code_expiration_minutes: 10,
  parameter_format: 'POSITIONAL',
  header_format: 'NONE',
  header_text: '',
  header_handle: '',
  body: '',
  footer: '',
  buttons: [],
  message_send_ttl_seconds: null,
});

const CATEGORY_ICONS = {
  MARKETING: 'i-hugeicons-megaphone-01',
  UTILITY: 'i-hugeicons-notification-02',
  AUTHENTICATION: 'i-hugeicons-lock-password',
};

export default {
  components: {
    SystemParameterMenu,
    TemplateButtonsField,
    MessageValidityField,
    AiTemplateDraft,
    MessageCardPreview,
    NextButton,
    NextInput,
    NextSelect,
    NextTextArea,
  },
  props: {
    createTemplate: { type: Function, default: null },
    creationContext: { type: String, default: '' },
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['busy'],
  data() {
    return {
      form: initialForm(),
      aiGenerationSession: 0,
      selectedTemplateId: '',
      activeView: 'templates',
      editorSection: 'BASIC',
      showTemplateModal: false,
      showDeleteConfirmModal: false,
      showFeedbackModal: false,
      feedbackTitle: '',
      feedbackMessage: '',
      feedbackTone: 'info',
      isSaving: false,
      mediaUploading: false,
      mediaFilename: '',
      mediaFile: null,
      mediaPreviewUrl: '',
      mediaError: '',
      mediaRequestId: 0,
      isSyncing: false,
      deletingTemplateId: '',
      templatePendingDelete: null,
    };
  },
  computed: {
    aiExistingDraft() {
      return existingTemplateDraft({
        name: this.form.name,
        body: this.form.body,
        header: this.form.header_format === 'TEXT' ? this.form.header_text : '',
        footer: this.typeConfig.allowsFooter ? this.form.footer : '',
        button_texts:
          this.currentType === TEMPLATE_TYPES.STANDARD
            ? this.form.buttons.map(button => button.text)
            : [],
        action_text: this.isOrderDetailsType
          ? this.form.order_details_button_text
          : '',
      });
    },
    mediaConfig() {
      return {
        IMAGE: {
          accept: 'image/jpeg,image/png',
          limit: 5,
          hint: 'JPG ou PNG · até 5 MB',
        },
        VIDEO: { accept: 'video/mp4', limit: 16, hint: 'MP4 · até 16 MB' },
        DOCUMENT: {
          accept: 'application/pdf',
          limit: 20,
          hint: 'PDF · até 20 MB',
        },
      }[this.form.header_format];
    },
    editorSections() {
      return [
        {
          key: 'BASIC',
          label: this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.SECTIONS.BASIC'),
        },
        {
          key: 'CONTENT',
          label: this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.SECTIONS.CONTENT'),
        },
        {
          key: 'ACTIONS',
          label: this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.SECTIONS.ACTIONS'),
        },
      ];
    },
    currentStepIndex() {
      return this.editorSections.findIndex(
        section => section.key === this.editorSection
      );
    },
    isLastStep() {
      return this.currentStepIndex === this.editorSections.length - 1;
    },
    canAdvance() {
      if (this.isSaving || this.isLastStep) return false;
      if (this.editorSection === 'BASIC') return this.isIdentificationValid;
      return this.editorSection === 'CONTENT' && this.isContentValid;
    },
    languageOptions() {
      return ['pt_BR', 'en_US'].map(value => ({
        value,
        label: this.languageLabel(value),
      }));
    },
    templates() {
      const templates =
        this.inbox.message_templates ||
        this.inbox.additional_attributes?.message_templates ||
        [];

      return Array.isArray(templates) ? templates : [];
    },
    sortedTemplates() {
      return [...this.templates].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '')
      );
    },
    selectedTemplate() {
      return this.templates.find(
        template => String(template.id) === String(this.selectedTemplateId)
      );
    },
    isEditing() {
      return !!this.form.id;
    },
    isAuthentication() {
      return this.form.category === 'AUTHENTICATION';
    },
    currentType() {
      if (this.isAuthentication) return TEMPLATE_TYPES.AUTHENTICATION;
      if (
        isTypeAllowedInCategory(this.form.template_type, this.form.category, {
          editing: this.isEditing,
        })
      )
        return this.form.template_type;
      return defaultTypeForCategory(this.form.category);
    },
    typeConfig() {
      return getTemplateTypeConfig(this.currentType);
    },
    isStandardType() {
      return this.currentType === TEMPLATE_TYPES.STANDARD;
    },
    isCatalogType() {
      return this.currentType === TEMPLATE_TYPES.CATALOG;
    },
    isOrderDetailsType() {
      return this.currentType === TEMPLATE_TYPES.ORDER_DETAILS;
    },
    isOrderStatusType() {
      return this.currentType === TEMPLATE_TYPES.ORDER_STATUS;
    },
    isCallPermissionType() {
      return this.currentType === TEMPLATE_TYPES.CALL_PERMISSION_REQUEST;
    },
    categoryTabs() {
      return TEMPLATE_CATEGORIES.map(category => ({
        value: category,
        icon: CATEGORY_ICONS[category],
        label: this.$t(`INBOX_MGMT.WHATSAPP_TEMPLATES.CATEGORIES.${category}`),
        disabled:
          this.isEditing &&
          !isTypeAllowedInCategory(this.currentType, category, {
            editing: true,
          }),
      }));
    },
    typeOptions() {
      return typesForCategory(this.form.category, {
        editing: this.isEditing,
      }).map(type => {
        const config = getTemplateTypeConfig(type);
        const descriptionKey =
          type === TEMPLATE_TYPES.STANDARD
            ? `DESCRIPTION_${this.form.category}`
            : 'DESCRIPTION';
        return {
          value: type,
          icon: config.icon,
          title: this.$t(
            `INBOX_MGMT.WHATSAPP_TEMPLATES.TYPES.${config.key}.TITLE`
          ),
          description: this.$t(
            `INBOX_MGMT.WHATSAPP_TEMPLATES.TYPES.${config.key}.${descriptionKey}`
          ),
          disabled: this.isEditing && type !== this.currentType,
        };
      });
    },
    typeNotice() {
      const notices = {
        [TEMPLATE_TYPES.CATALOG]: 'CATALOG',
        [TEMPLATE_TYPES.ORDER_DETAILS]: 'ORDER_DETAILS',
        [TEMPLATE_TYPES.ORDER_STATUS]: 'ORDER_STATUS',
        [TEMPLATE_TYPES.CALL_PERMISSION_REQUEST]: 'CALL_PERMISSION_REQUEST',
      };
      const key = notices[this.currentType];
      return key
        ? this.$t(`INBOX_MGMT.WHATSAPP_TEMPLATES.TYPE_NOTICES.${key}`)
        : '';
    },
    catalogsRoute() {
      const router = this.$router;
      try {
        if (!router?.hasRoute?.('whatsapp_catalogs')) return null;
        return {
          name: 'whatsapp_catalogs',
          params: { accountId: this.$route?.params?.accountId },
        };
      } catch {
        return null;
      }
    },
    headerOptions() {
      return this.typeConfig.headerFormats.map(format => ({
        value: format,
        label: this.$t(`INBOX_MGMT.WHATSAPP_TEMPLATES.HEADER_TYPES.${format}`),
      }));
    },
    allowsHeader() {
      return this.typeConfig.headerFormats.length > 1;
    },
    orderButtonMaxLength() {
      return ORDER_DETAILS_BUTTON_MAX_LENGTH;
    },
    orderButtonLength() {
      return (this.form.order_details_button_text || '').length;
    },
    isOrderButtonValid() {
      if (!this.isOrderDetailsType) return true;
      const text = (this.form.order_details_button_text || '').trim();
      return (
        Boolean(text) &&
        this.orderButtonLength <= ORDER_DETAILS_BUTTON_MAX_LENGTH
      );
    },
    isIdentificationValid() {
      return (
        Boolean(this.form.name.trim()) &&
        Boolean(this.form.language.trim()) &&
        TEMPLATE_CATEGORIES.includes(this.form.category) &&
        isTypeAllowedInCategory(this.currentType, this.form.category, {
          editing: this.isEditing,
        })
      );
    },
    isContentValid() {
      if (this.mediaUploading) return false;
      if (this.isAuthentication)
        return (
          Number.isInteger(Number(this.form.code_expiration_minutes)) &&
          Number(this.form.code_expiration_minutes) >= 1 &&
          Number(this.form.code_expiration_minutes) <= 90
        );
      if (this.mediaConfig && !this.form.header_handle) return false;
      return (
        Boolean(this.form.body.trim()) &&
        this.isHeaderValid &&
        this.isBodyValid &&
        this.isFooterValid
      );
    },
    canSave() {
      return (
        this.isIdentificationValid &&
        this.isContentValid &&
        this.isOrderButtonValid
      );
    },
    headerLength() {
      return this.form.header_text.length;
    },
    isHeaderValid() {
      if (this.isAuthentication) return true;
      return (
        this.form.header_format !== 'TEXT' ||
        this.headerLength <= HEADER_TEXT_MAX_LENGTH
      );
    },
    headerMaxLength() {
      return HEADER_TEXT_MAX_LENGTH;
    },
    bodyLength() {
      return this.form.body.length;
    },
    isBodyValid() {
      if (this.isAuthentication) return true;
      return this.bodyLength <= BODY_MAX_LENGTH;
    },
    bodyMaxLength() {
      return BODY_MAX_LENGTH;
    },
    footerLength() {
      return this.form.footer.length;
    },
    isFooterValid() {
      if (this.isAuthentication) return true;
      return this.footerLength <= FOOTER_MAX_LENGTH;
    },
    footerMaxLength() {
      return FOOTER_MAX_LENGTH;
    },
    statusCounts() {
      return this.templates.reduce((acc, template) => {
        const status = (template.status || 'PENDING').toUpperCase();
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {});
    },
    approvalRate() {
      if (!this.templates.length) return 0;
      return Math.round(
        ((this.statusCounts.APPROVED || 0) / this.templates.length) * 100
      );
    },
    statusInsights() {
      return [
        {
          key: 'APPROVED',
          count: this.statusCounts.APPROVED || 0,
          barClass: 'bg-n-teal-9',
          dotClass: 'bg-n-teal-9',
        },
        {
          key: 'PENDING',
          count: this.statusCounts.PENDING || 0,
          barClass: 'bg-n-amber-9',
          dotClass: 'bg-n-amber-9',
        },
        {
          key: 'REJECTED',
          count: this.statusCounts.REJECTED || 0,
          barClass: 'bg-n-ruby-9',
          dotClass: 'bg-n-ruby-9',
        },
        {
          key: 'PAUSED',
          count: this.statusCounts.PAUSED || 0,
          barClass: 'bg-n-slate-8',
          dotClass: 'bg-n-slate-8',
        },
      ];
    },
    categoryCounts() {
      return this.templates.reduce((acc, template) => {
        const category = template.category || 'UNKNOWN';
        acc[category] = (acc[category] || 0) + 1;
        return acc;
      }, {});
    },
    languageCounts() {
      return this.templates.reduce((acc, template) => {
        const language = template.language || 'UNKNOWN';
        acc[language] = (acc[language] || 0) + 1;
        return acc;
      }, {});
    },
    lastSyncLabel() {
      const lastSync =
        this.inbox.message_templates_last_updated ||
        this.inbox.additional_attributes?.message_templates_last_updated;

      if (!lastSync)
        return this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.INSIGHTS.NEVER');
      return new Date(lastSync).toLocaleString();
    },
    previewHeader() {
      if (!this.allowsHeader || this.form.header_format !== 'TEXT') return '';
      return this.replaceVariables(this.form.header_text);
    },
    previewBody() {
      if (this.isAuthentication)
        return `123456 é seu código de verificação.${this.form.add_security_recommendation ? ' Para sua segurança, não compartilhe esse código.' : ''}`;
      return this.replaceVariables(this.form.body);
    },
    bodyPlaceholder() {
      return this.form.parameter_format === 'NAMED'
        ? this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.PLACEHOLDERS.BODY_NAMED')
        : this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.PLACEHOLDERS.BODY');
    },
    variableHint() {
      return this.form.parameter_format === 'NAMED'
        ? this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.VARIABLE_HINT_NAMED')
        : this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.VARIABLE_HINT_POSITIONAL');
    },
    previewFooter() {
      if (this.isAuthentication)
        return `Este código expira em ${this.form.code_expiration_minutes} minutos.`;
      if (!this.typeConfig.allowsFooter) return '';
      return this.replaceVariables(this.form.footer);
    },
    visibleButtons() {
      if (this.isAuthentication)
        return [
          {
            type: 'OTP',
            text:
              this.form.language === 'en_US' ? 'Copy code' : 'Copiar código',
          },
        ];
      if (this.isCatalogType)
        return [
          {
            type: 'CATALOG',
            icon: 'i-hugeicons-store-01',
            text: this.$t(
              'INBOX_MGMT.WHATSAPP_TEMPLATES.PREVIEW.CATALOG_BUTTON'
            ),
          },
        ];
      if (this.isOrderDetailsType)
        return [
          {
            type: 'ORDER_DETAILS',
            icon: 'i-hugeicons-copy-01',
            text:
              this.form.order_details_button_text.trim() ||
              defaultOrderDetailsButtonText(this.form.language),
          },
        ];
      if (!this.isStandardType) return [];
      return this.form.buttons.filter(hasButtonContent).map(button => ({
        ...button,
        text: button.type === 'COPY_CODE' ? COPY_CODE_TEXT : button.text,
      }));
    },
  },
  watch: {
    isSaving(value) {
      this.$emit('busy', value);
    },
    editorSection() {
      this.$nextTick(() => {
        if (this.$refs.templateModalBody)
          this.$refs.templateModalBody.scrollTop = 0;
      });
    },
    'form.header_format'() {
      this.clearMediaUpload();
    },
    currentType() {
      if (!this.typeConfig.headerFormats.includes(this.form.header_format))
        this.form.header_format = 'NONE';
    },
    'form.language'(language, previousLanguage) {
      if (
        this.form.order_details_button_text ===
        defaultOrderDetailsButtonText(previousLanguage)
      )
        this.form.order_details_button_text =
          defaultOrderDetailsButtonText(language);
    },
    'inbox.id'() {
      this.clearMediaUpload();
    },
    showTemplateModal(open) {
      if (!open) this.clearMediaUpload();
    },
  },
  beforeUnmount() {
    this.clearMediaPreview();
    this.mediaRequestId += 1;
  },
  mounted() {
    this.refreshInbox();
  },
  methods: {
    nextEditorSection() {
      if (!this.canAdvance) return;
      this.editorSection = this.editorSections[this.currentStepIndex + 1].key;
    },
    previousEditorSection() {
      if (this.isSaving || this.currentStepIndex <= 0) return;
      this.editorSection = this.editorSections[this.currentStepIndex - 1].key;
    },
    generateAiDraft(options) {
      return InboxesAPI.generateWhatsappTemplate(this.inbox.id, options);
    },
    applyAiDraft(draft) {
      if (!this.isEditing) this.form.name = draft.name;
      this.form.body = draft.body;
      this.form.footer = draft.footer;
      if (this.form.header_format === 'TEXT')
        this.form.header_text = draft.header;
      if (this.currentType === TEMPLATE_TYPES.STANDARD) {
        const linkedButtons = this.form.buttons.filter(
          button => button.type !== 'QUICK_REPLY'
        );
        this.form.buttons = [
          ...linkedButtons,
          ...draft.button_texts
            .slice(0, 3 - linkedButtons.length)
            .map(text => ({ ...emptyButton(), text })),
        ];
      }
      if (this.isOrderDetailsType && draft.action_text)
        this.form.order_details_button_text = draft.action_text;
      this.editorSection = 'CONTENT';
    },
    onTemplateNameInput(event) {
      const input = event.target;
      const value = input.value;
      const start = normalizeTemplateName(
        value.slice(0, input.selectionStart ?? value.length)
      ).length;
      const end = normalizeTemplateName(
        value.slice(0, input.selectionEnd ?? value.length)
      ).length;
      this.form.name = normalizeTemplateName(value);
      if (input.value !== this.form.name) {
        input.value = this.form.name;
        input.setSelectionRange(start, end);
      }
    },
    clearMediaPreview() {
      if (this.mediaPreviewUrl) URL.revokeObjectURL(this.mediaPreviewUrl);
      this.mediaPreviewUrl = '';
    },
    clearMediaUpload() {
      this.clearMediaPreview();
      this.mediaRequestId += 1;
      this.mediaUploading = false;
      this.mediaFilename = '';
      this.mediaFile = null;
      this.mediaError = '';
      this.form.header_handle = '';
    },
    async uploadHeaderMedia(event) {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file || !this.mediaConfig) return;
      this.clearMediaUpload();
      if (
        !this.mediaConfig.accept.split(',').includes(file.type) ||
        !file.size ||
        file.size > this.mediaConfig.limit * 1024 * 1024
      ) {
        this.mediaError = this.$t(
          'INBOX_MGMT.WHATSAPP_TEMPLATES.MEDIA.INVALID',
          { formats: this.mediaConfig.hint }
        );
        return;
      }
      if (this.form.header_format === 'IMAGE') {
        this.mediaPreviewUrl = URL.createObjectURL(file);
      }
      const requestId = this.mediaRequestId;
      const targetForm = this.form;
      this.mediaUploading = true;
      try {
        const { data } = await InboxesAPI.uploadWhatsappTemplateMedia(
          this.inbox.id,
          file,
          this.form.header_format
        );
        if (requestId !== this.mediaRequestId || targetForm !== this.form)
          return;
        if (!data.handle) throw new Error('missing handle');
        this.form.header_handle = data.handle;
        this.mediaFile = file;
        this.mediaFilename = file.name;
      } catch {
        if (requestId === this.mediaRequestId && targetForm === this.form)
          this.mediaError = this.$t(
            'INBOX_MGMT.WHATSAPP_TEMPLATES.MEDIA.ERROR'
          );
      } finally {
        if (requestId === this.mediaRequestId) this.mediaUploading = false;
      }
    },
    getComponent(template, type) {
      return template?.components?.find(component => component.type === type);
    },
    getButtons(template) {
      return this.getComponent(template, 'BUTTONS')?.buttons || [];
    },
    getBodyPreview(template) {
      return this.getComponent(template, 'BODY')?.text || '';
    },
    templateTypeLabel(template) {
      const type = detectWhatsappTemplateType(template);
      if (type === TEMPLATE_TYPES.STANDARD) return '';
      return this.$t(
        `INBOX_MGMT.WHATSAPP_TEMPLATES.TYPES.${getTemplateTypeConfig(type).key}.TITLE`
      );
    },
    selectCategory(category) {
      const tab = this.categoryTabs.find(item => item.value === category);
      if (!tab || tab.disabled || this.form.category === category) return;
      this.form.category = category;
      if (
        !isTypeAllowedInCategory(this.form.template_type, category, {
          editing: this.isEditing,
        })
      )
        this.form.template_type = defaultTypeForCategory(category);
    },
    selectType(type) {
      const option = this.typeOptions.find(item => item.value === type);
      if (!option || option.disabled) return;
      this.form.template_type = type;
    },
    templateMeta(template) {
      return [
        this.categoryLabel(template.category),
        this.languageLabel(template.language),
      ].join(' / ');
    },
    statusClass(status) {
      const normalizedStatus = status?.toLowerCase();
      if (normalizedStatus === 'approved') return 'text-n-teal-11 bg-n-teal-3';
      if (normalizedStatus === 'rejected') return 'text-n-ruby-11 bg-n-ruby-3';
      return 'text-n-amber-11 bg-n-amber-3';
    },
    statusLabel(status) {
      const key = String(status || '').toUpperCase();
      const known = [
        'APPROVED',
        'PENDING',
        'REJECTED',
        'PAUSED',
        'DISABLED',
        'IN_APPEAL',
        'PENDING_DELETION',
        'DELETED',
        'LIMIT_EXCEEDED',
      ];
      return this.$t(
        `INBOX_MGMT.WHATSAPP_TEMPLATES.STATUS_LABELS.${known.includes(key) ? key : 'UNKNOWN'}`
      );
    },
    categoryLabel(category) {
      const key = String(category || '').toUpperCase();
      return this.$t(
        `INBOX_MGMT.WHATSAPP_TEMPLATES.CATEGORIES.${TEMPLATE_CATEGORIES.includes(key) ? key : 'UNKNOWN'}`
      );
    },
    languageLabel(language) {
      if (!language || language === 'UNKNOWN') {
        return this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.LANGUAGES.UNKNOWN');
      }
      const code = language.replaceAll('_', '-');
      if (code === 'pt-BR' || code === 'en-US') {
        return this.$t(`INBOX_MGMT.WHATSAPP_TEMPLATES.LANGUAGES.${code}`);
      }
      try {
        const locale = (this.$i18n?.locale || 'pt-BR').replaceAll('_', '-');
        return new Intl.DisplayNames([locale], { type: 'language' }).of(code);
      } catch {
        return language;
      }
    },
    statusPercentage(count) {
      if (!this.templates.length) return 0;
      return Math.round((count / this.templates.length) * 100);
    },
    replaceVariables(text) {
      return text
        .toString()
        .replace(/\{\{\s*([^}]+?)\s*\}\}/g, (_match, variable) => {
          const normalizedVariable = variable.trim();
          if (/^\d+$/.test(normalizedVariable)) {
            return `exemplo ${normalizedVariable}`;
          }

          return (
            systemParameterExample(normalizedVariable) ||
            `exemplo ${normalizedVariable}`
          );
        });
    },
    // Com variáveis nomeadas, os parâmetros do sistema entram como {{primeiro_nome}}
    // e são preenchidos automaticamente no envio do template.
    insertBodyParameter(token) {
      const element = this.$refs.bodyField?.querySelector('textarea');
      const { value, caret } = insertAtCursor(this.form.body, token, element);
      this.form.body = value;
      this.$nextTick(() => {
        element?.focus();
        element?.setSelectionRange?.(caret, caret);
      });
    },
    openCreateModal() {
      this.aiGenerationSession += 1;
      this.clearMediaUpload();
      this.editorSection = 'BASIC';
      this.form = initialForm();
      this.selectedTemplateId = '';
      this.showTemplateModal = true;
    },
    openEditModal(template) {
      this.aiGenerationSession += 1;
      this.editorSection = 'CONTENT';
      this.selectTemplate(template);
      this.showTemplateModal = true;
    },
    closeTemplateModal() {
      if (this.isSaving) return;
      this.showTemplateModal = false;
    },
    selectTemplate(template) {
      this.clearMediaUpload();
      this.selectedTemplateId = template.id;
      const header = this.getComponent(template, 'HEADER');
      const body = this.getComponent(template, 'BODY');
      const footer = this.getComponent(template, 'FOOTER');
      const templateType = detectWhatsappTemplateType(template);
      const language = template.language || 'pt_BR';

      this.form = {
        id: template.id,
        name: template.name || '',
        language,
        category: template.category || 'UTILITY',
        template_type: templateType,
        order_details_button_text:
          orderDetailsButtonText(template) ||
          defaultOrderDetailsButtonText(language),
        add_security_recommendation: body?.add_security_recommendation ?? true,
        code_expiration_minutes: footer?.code_expiration_minutes || 10,
        parameter_format: template.parameter_format || 'POSITIONAL',
        header_format: header?.format || 'NONE',
        header_text: header?.text || '',
        header_handle: '',
        body: body?.text || '',
        footer: footer?.text || '',
        buttons: (templateType === TEMPLATE_TYPES.STANDARD
          ? this.getButtons(template)
          : []
        ).map(templateButtonToForm),
        message_send_ttl_seconds: template.message_send_ttl_seconds || null,
      };
    },
    buildPayload() {
      return buildWhatsappTemplatePayload(this.form, this.currentType);
    },
    openFeedbackModal(title, message, tone = 'info') {
      this.feedbackTitle = title;
      this.feedbackMessage = message;
      this.feedbackTone = tone;
      this.showFeedbackModal = true;
    },
    closeFeedbackModal() {
      this.showFeedbackModal = false;
    },
    async saveTemplate() {
      if (!this.isLastStep || this.isSaving) return;
      if (
        this.mediaUploading ||
        (!this.isAuthentication && this.mediaConfig && !this.form.header_handle)
      )
        return;
      if (!this.isHeaderValid) {
        useAlert(
          this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.HEADER_LIMIT_ERROR', {
            max: HEADER_TEXT_MAX_LENGTH,
          })
        );
        return;
      }
      if (!this.isBodyValid) {
        useAlert(
          this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.BODY_LIMIT_ERROR', {
            max: BODY_MAX_LENGTH,
          })
        );
        return;
      }
      if (!this.isFooterValid) {
        useAlert(
          this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.FOOTER_LIMIT_ERROR', {
            max: FOOTER_MAX_LENGTH,
          })
        );
        return;
      }
      if (!this.isOrderButtonValid) {
        useAlert(
          this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.ORDER_BUTTON_LIMIT_ERROR', {
            max: ORDER_DETAILS_BUTTON_MAX_LENGTH,
          })
        );
        return;
      }
      if (!this.canSave) return;
      this.isSaving = true;
      try {
        if (!this.isEditing && this.createTemplate) {
          await this.createTemplate(this.buildPayload(), this.mediaFile);
          this.isSaving = false;
          this.closeTemplateModal();
          return;
        }
        const action = this.isEditing
          ? 'inboxes/updateWhatsappTemplate'
          : 'inboxes/createWhatsappTemplate';
        const payload = this.isEditing
          ? {
              inboxId: this.inbox.id,
              templateId: this.form.id,
              template: this.buildPayload(),
            }
          : { inboxId: this.inbox.id, template: this.buildPayload() };

        await this.$store.dispatch(action, payload);
        useAlert(
          this.isEditing
            ? this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.UPDATE_SUCCESS')
            : this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.CREATE_SUCCESS')
        );
        this.isSaving = false;
        this.closeTemplateModal();
        await this.syncTemplates();
      } catch (error) {
        useAlert(
          getWhatsappTemplateErrorMessage(
            error,
            this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.SAVE_ERROR')
          )
        );
      } finally {
        this.isSaving = false;
      }
    },
    openDeleteConfirmModal(template) {
      this.templatePendingDelete = template;
      this.showDeleteConfirmModal = true;
    },
    closeDeleteConfirmModal() {
      this.showDeleteConfirmModal = false;
      this.templatePendingDelete = null;
    },
    async deleteTemplate() {
      const template = this.templatePendingDelete;
      if (!template) return;

      const templateId = template.id || template.name;
      if (!templateId) return;

      this.deletingTemplateId = String(templateId);
      try {
        await this.$store.dispatch('inboxes/deleteWhatsappTemplate', {
          inboxId: this.inbox.id,
          templateId,
        });
        this.closeDeleteConfirmModal();
        this.openFeedbackModal(
          this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.SUCCESS_TITLE'),
          this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE_SUCCESS'),
          'success'
        );
        await this.refreshInbox();
      } catch (error) {
        this.openFeedbackModal(
          this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.ERROR_TITLE'),
          error?.response?.data?.error ||
            this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE_ERROR'),
          'error'
        );
      } finally {
        this.deletingTemplateId = '';
      }
    },
    async syncTemplates() {
      this.isSyncing = true;
      try {
        await this.$store.dispatch('inboxes/syncTemplates', this.inbox.id);
        setTimeout(() => this.refreshInbox(), 1200);
        await this.refreshInbox();
      } catch (error) {
        this.openFeedbackModal(
          this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.ERROR_TITLE'),
          this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'),
          'error'
        );
      } finally {
        this.isSyncing = false;
      }
    },
    async refreshInbox() {
      await this.$store.dispatch('inboxes/getFromNetwork');
    },
  },
};
</script>

<template>
  <div class="mx-3 sm:mx-6">
    <div
      class="mb-5 flex flex-col justify-between gap-4 lg:flex-row lg:items-start"
    >
      <div>
        <h2 class="text-heading-1 text-n-slate-12 mb-1">
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.TITLE') }}
        </h2>
        <p class="text-sm text-n-slate-11 max-w-3xl">
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.DESCRIPTION') }}
        </p>
      </div>
      <div class="flex gap-2">
        <NextButton
          ghost
          sm
          icon="i-lucide-refresh-cw"
          :disabled="isSyncing"
          :is-loading="isSyncing"
          :label="
            isSyncing
              ? $t('INBOX_MGMT.WHATSAPP_TEMPLATES.SYNCING')
              : $t('INBOX_MGMT.WHATSAPP_TEMPLATES.SYNC')
          "
          @click="syncTemplates"
        />
        <NextButton
          sm
          icon="i-lucide-plus"
          :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.CREATE')"
          @click="openCreateModal"
        />
      </div>
    </div>

    <div
      class="flex items-start gap-3 p-4 mb-5 rounded-xl bg-n-blue-2 outline outline-1 outline-n-blue-5"
    >
      <span
        class="flex items-center justify-center size-8 rounded-lg bg-n-blue-4 text-n-blue-11 shrink-0"
      >
        <i class="i-lucide-clock-3 size-4" />
      </span>
      <div class="min-w-0">
        <p class="mb-1 text-sm font-semibold text-n-slate-12">
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.REVIEW_NOTICE_TITLE') }}
        </p>
        <p class="m-0 text-sm leading-5 text-n-slate-11">
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.REVIEW_NOTICE_DESCRIPTION') }}
        </p>
      </div>
    </div>

    <div
      class="inline-flex items-center gap-1 p-1 mb-5 rounded-lg bg-n-alpha-2 outline outline-1 outline-n-weak"
    >
      <button
        type="button"
        class="px-3 py-1.5 text-sm rounded-md transition-colors"
        :class="
          activeView === 'templates'
            ? 'bg-n-background text-n-slate-12 shadow-sm'
            : 'text-n-slate-11 hover:text-n-slate-12'
        "
        @click="activeView = 'templates'"
      >
        {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.TABS.TEMPLATES') }}
        <span class="ml-1 text-xs text-n-slate-10">{{ templates.length }}</span>
      </button>
      <button
        type="button"
        class="px-3 py-1.5 text-sm rounded-md transition-colors"
        :class="
          activeView === 'insights'
            ? 'bg-n-background text-n-slate-12 shadow-sm'
            : 'text-n-slate-11 hover:text-n-slate-12'
        "
        @click="activeView = 'insights'"
      >
        {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.TABS.INSIGHTS') }}
      </button>
    </div>

    <section v-if="activeView === 'templates'">
      <div
        v-if="!sortedTemplates.length"
        class="rounded-2xl border border-dashed border-n-strong p-10 text-center text-sm text-n-slate-11"
      >
        {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.EMPTY') }}
      </div>
      <div
        v-else
        class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4"
      >
        <article
          v-for="template in sortedTemplates"
          :key="template.id || `${template.name}-${template.language}`"
          class="group relative flex min-w-0 cursor-pointer flex-col gap-4 rounded-2xl border border-n-weak bg-n-solid-1 p-5 shadow-sm transition-colors hover:border-n-brand/50 hover:bg-n-alpha-1"
        >
          <button
            type="button"
            class="absolute inset-0 cursor-pointer rounded-2xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
            :aria-label="`${$t('INBOX_MGMT.WHATSAPP_TEMPLATES.EDIT')}: ${template.name}`"
            @click="openEditModal(template)"
          />
          <div class="flex items-center justify-between gap-2">
            <span
              class="grid size-10 shrink-0 place-items-center rounded-xl bg-n-brand/10 text-n-brand"
            >
              <i class="i-lucide-message-square-text size-5" />
            </span>
            <span
              class="rounded-md px-2 py-1 text-xs font-medium"
              :class="statusClass(template.status)"
            >
              {{ statusLabel(template.status) }}
            </span>
          </div>
          <h3 class="m-0 truncate text-sm font-semibold text-n-slate-12">
            {{ template.name }}
          </h3>
          <MessageCardPreview
            :body="getBodyPreview(template)"
            :header="getComponent(template, 'HEADER')?.text"
            :media-type="
              getComponent(template, 'HEADER')?.format?.toLowerCase()
            "
            :footer="getComponent(template, 'FOOTER')?.text"
            :buttons="getButtons(template)"
          />
          <div
            class="mt-auto flex items-center justify-between gap-2 border-t border-n-weak pt-3"
          >
            <div class="flex min-w-0 flex-wrap gap-1 text-xs text-n-slate-11">
              <span class="rounded-md bg-n-alpha-2 px-2 py-1">{{
                categoryLabel(template.category)
              }}</span>
              <span class="rounded-md bg-n-alpha-2 px-2 py-1">{{
                languageLabel(template.language)
              }}</span>
              <span
                v-if="templateTypeLabel(template)"
                class="rounded-md bg-n-alpha-2 px-2 py-1"
              >
                {{ templateTypeLabel(template) }}
              </span>
            </div>
            <NextButton
              class="relative z-10 shrink-0"
              ghost
              xs
              ruby
              icon="i-lucide-trash-2"
              :disabled="
                deletingTemplateId === String(template.id || template.name)
              "
              :aria-label="`${$t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE')}: ${template.name}`"
              @click.stop="openDeleteConfirmModal(template)"
            />
          </div>
        </article>
      </div>
    </section>

    <section v-else class="max-w-6xl space-y-4">
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <div
          class="p-4 bg-n-surface-1 outline outline-1 outline-n-weak rounded-xl"
        >
          <div class="text-xs text-n-slate-10">
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.INSIGHTS.TOTAL') }}
          </div>
          <div class="text-2xl font-semibold text-n-slate-12">
            {{ templates.length }}
          </div>
        </div>
        <div
          class="p-4 bg-n-surface-1 outline outline-1 outline-n-weak rounded-xl"
        >
          <div class="text-xs text-n-slate-10">
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.INSIGHTS.APPROVED') }}
          </div>
          <div class="text-2xl font-semibold text-n-teal-11">
            {{ statusCounts.APPROVED || 0 }}
          </div>
        </div>
        <div
          class="p-4 bg-n-surface-1 outline outline-1 outline-n-weak rounded-xl"
        >
          <div class="text-xs text-n-slate-10">
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.INSIGHTS.PENDING') }}
          </div>
          <div class="text-2xl font-semibold text-n-amber-11">
            {{ statusCounts.PENDING || 0 }}
          </div>
        </div>
        <div
          class="p-4 bg-n-surface-1 outline outline-1 outline-n-weak rounded-xl"
        >
          <div class="text-xs text-n-slate-10">
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.INSIGHTS.REJECTED') }}
          </div>
          <div class="text-2xl font-semibold text-n-ruby-11">
            {{ statusCounts.REJECTED || 0 }}
          </div>
        </div>
        <div
          class="p-4 bg-n-surface-1 outline outline-1 outline-n-weak rounded-xl"
        >
          <div class="text-xs text-n-slate-10">
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.INSIGHTS.APPROVAL_RATE') }}
          </div>
          <div class="text-2xl font-semibold text-n-slate-12">
            {{
              $t('INBOX_MGMT.WHATSAPP_TEMPLATES.INSIGHTS.PERCENTAGE', {
                value: approvalRate,
              })
            }}
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 gap-4 lg:grid-cols-[1.1fr_0.9fr]">
        <div
          class="p-5 bg-n-surface-1 outline outline-1 outline-n-weak rounded-xl"
        >
          <div class="flex items-center justify-between gap-4 mb-4">
            <h3 class="m-0 text-sm font-semibold text-n-slate-12">
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.INSIGHTS.BY_STATUS') }}
            </h3>
            <span class="text-xs text-n-slate-10">
              {{
                $t('INBOX_MGMT.WHATSAPP_TEMPLATES.INSIGHTS.UPDATED_AT_VALUE', {
                  value: lastSyncLabel,
                })
              }}
            </span>
          </div>
          <div class="space-y-4">
            <div v-for="item in statusInsights" :key="item.key">
              <div
                class="flex items-center justify-between gap-3 mb-1.5 text-sm"
              >
                <span class="flex items-center gap-2 text-n-slate-11">
                  <span class="size-2 rounded-full" :class="item.dotClass" />
                  {{ statusLabel(item.key) }}
                </span>
                <span class="font-medium text-n-slate-12">
                  {{
                    $t('INBOX_MGMT.WHATSAPP_TEMPLATES.INSIGHTS.STATUS_VALUE', {
                      count: item.count,
                      percentage: statusPercentage(item.count),
                    })
                  }}
                </span>
              </div>
              <div class="h-2 overflow-hidden rounded-full bg-n-alpha-3">
                <div
                  class="h-full rounded-full transition-[width] duration-300"
                  :class="item.barClass"
                  :style="{ width: `${statusPercentage(item.count)}%` }"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-1">
          <div
            class="p-5 bg-n-surface-1 outline outline-1 outline-n-weak rounded-xl"
          >
            <h3 class="text-sm font-semibold text-n-slate-12 mb-3">
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.INSIGHTS.BY_CATEGORY') }}
            </h3>
            <div
              v-for="(count, category) in categoryCounts"
              :key="category"
              class="flex justify-between py-2 text-sm border-b border-n-weak last:border-b-0"
            >
              <span class="text-n-slate-11">{{ categoryLabel(category) }}</span>
              <span class="font-medium text-n-slate-12">{{ count }}</span>
            </div>
          </div>
          <div
            class="p-5 bg-n-surface-1 outline outline-1 outline-n-weak rounded-xl"
          >
            <h3 class="text-sm font-semibold text-n-slate-12 mb-3">
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.INSIGHTS.BY_LANGUAGE') }}
            </h3>
            <div
              v-for="(count, language) in languageCounts"
              :key="language"
              class="flex justify-between py-2 text-sm border-b border-n-weak last:border-b-0"
            >
              <span class="text-n-slate-11">{{ languageLabel(language) }}</span>
              <span class="font-medium text-n-slate-12">{{ count }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <woot-modal
      :show="showTemplateModal"
      :show-close-button="!isSaving"
      :close-on-backdrop-click="!isSaving"
      size="whatsapp-template-modal-shell"
      :on-close="closeTemplateModal"
      @update:show="closeTemplateModal"
    >
      <div class="whatsapp-template-modal-header">
        <span class="whatsapp-template-modal-icon">
          <i class="i-lucide-message-square-text size-5" />
        </span>
        <div>
          <h2 class="mb-1 text-base font-semibold text-n-slate-12">
            {{
              isEditing
                ? $t('INBOX_MGMT.WHATSAPP_TEMPLATES.EDIT_TITLE')
                : $t('INBOX_MGMT.WHATSAPP_TEMPLATES.CREATE_TITLE')
            }}
          </h2>
          <p class="m-0 text-xs text-n-slate-10">
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.MODAL_DESCRIPTION') }}
          </p>
        </div>
      </div>

      <p
        v-if="creationContext && !isEditing"
        class="m-0 border-b border-n-weak bg-n-teal-2 px-6 py-3 text-sm text-n-slate-12"
      >
        {{ creationContext }}
      </p>
      <nav
        class="whatsapp-template-steps"
        :aria-label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.CREATE_TITLE')"
      >
        <button
          v-for="(section, index) in editorSections"
          :key="section.key"
          type="button"
          :aria-current="editorSection === section.key ? 'step' : undefined"
          :disabled="isSaving"
          :class="{ 'is-active': editorSection === section.key }"
          @click="editorSection = section.key"
        >
          <span>{{ index + 1 }}</span>
          <strong class="font-medium">{{ section.label }}</strong>
        </button>
      </nav>
      <div ref="templateModalBody" class="whatsapp-template-modal-body">
        <div class="whatsapp-template-form">
          <AiTemplateDraft
            v-if="!isAuthentication && showTemplateModal"
            :key="aiGenerationSession"
            modal
            :generate="generateAiDraft"
            :context="{
              type: currentType,
              category: form.category,
              language: form.language,
              header_format: form.header_format,
              parameter_format: form.parameter_format,
              existing_draft: aiExistingDraft,
            }"
            :disabled="isSaving"
            @apply="applyAiDraft"
          />
          <p
            v-else-if="isAuthentication"
            class="m-0 rounded-xl border border-n-weak p-4 text-sm text-n-slate-11"
          >
            {{ $t('AI_TEMPLATE_DRAFT.AUTHENTICATION') }}
          </p>
          <section
            v-show="editorSection === 'BASIC'"
            class="whatsapp-template-section"
          >
            <div class="whatsapp-template-section-heading">
              <span class="whatsapp-template-section-icon">
                <i class="i-lucide-settings-2 size-4" />
              </span>
              <div>
                <h3>
                  {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.SECTIONS.BASIC') }}
                </h3>
                <p>
                  {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.SECTIONS.BASIC_HINT') }}
                </p>
              </div>
            </div>
            <div class="whatsapp-template-two-columns">
              <label class="whatsapp-template-field">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FIELDS.NAME') }}
                </span>
                <NextInput
                  :model-value="form.name"
                  autocapitalize="none"
                  :spellcheck="false"
                  :disabled="isEditing"
                  type="text"
                  class="mt-1 !mb-0"
                  :placeholder="
                    $t('INBOX_MGMT.WHATSAPP_TEMPLATES.PLACEHOLDERS.NAME')
                  "
                  @input="onTemplateNameInput"
                />
              </label>
              <label class="whatsapp-template-field">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FIELDS.LANGUAGE') }}
                </span>
                <NextSelect
                  v-model="form.language"
                  :disabled="isEditing"
                  :options="languageOptions"
                  class="w-full [&>select]:w-full"
                />
              </label>
            </div>

            <div class="whatsapp-template-field">
              <span
                id="whatsapp-template-category-label"
                class="text-sm font-medium text-n-slate-12"
              >
                {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FIELDS.CATEGORY') }}
              </span>
              <div
                role="tablist"
                aria-labelledby="whatsapp-template-category-label"
                class="grid grid-cols-1 gap-1 p-1 rounded-xl bg-n-alpha-2 outline outline-1 outline-n-weak sm:grid-cols-3"
                data-testid="template-category-tabs"
              >
                <button
                  v-for="tab in categoryTabs"
                  :key="tab.value"
                  type="button"
                  role="tab"
                  :aria-selected="form.category === tab.value"
                  :aria-disabled="tab.disabled"
                  :disabled="tab.disabled"
                  class="flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium transition-colors rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
                  :class="[
                    form.category === tab.value
                      ? 'bg-n-solid-1 text-n-slate-12 shadow-sm outline outline-1 outline-n-weak'
                      : 'text-n-slate-11 hover:text-n-slate-12',
                    tab.disabled
                      ? 'cursor-not-allowed opacity-50 hover:text-n-slate-11'
                      : 'cursor-pointer',
                  ]"
                  @click="selectCategory(tab.value)"
                >
                  <i
                    class="size-4 shrink-0"
                    :class="[
                      tab.icon,
                      form.category === tab.value ? 'text-n-brand' : '',
                    ]"
                    aria-hidden="true"
                  />
                  {{ tab.label }}
                </button>
              </div>
            </div>

            <div class="whatsapp-template-field">
              <span
                id="whatsapp-template-type-label"
                class="text-sm font-medium text-n-slate-12"
              >
                {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FIELDS.TEMPLATE_TYPE') }}
              </span>
              <div
                role="radiogroup"
                aria-labelledby="whatsapp-template-type-label"
                class="grid grid-cols-1 gap-2 md:grid-cols-2"
                data-testid="template-type-options"
              >
                <label
                  v-for="option in typeOptions"
                  :key="option.value"
                  class="relative flex items-start gap-3 p-3 transition-colors border rounded-xl"
                  :class="[
                    currentType === option.value
                      ? 'border-n-brand bg-n-brand/5'
                      : 'border-n-weak bg-n-solid-1 hover:border-n-strong',
                    option.disabled
                      ? 'cursor-not-allowed opacity-50'
                      : 'cursor-pointer',
                  ]"
                >
                  <input
                    type="radio"
                    name="whatsapp-template-type"
                    class="sr-only peer"
                    :value="option.value"
                    :checked="currentType === option.value"
                    :disabled="option.disabled"
                    @change="selectType(option.value)"
                  />
                  <span
                    class="grid size-8 shrink-0 place-items-center rounded-lg peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-n-brand"
                    :class="
                      currentType === option.value
                        ? 'bg-n-brand/10 text-n-brand'
                        : 'bg-n-alpha-2 text-n-slate-11'
                    "
                  >
                    <i class="size-4" :class="option.icon" aria-hidden="true" />
                  </span>
                  <span class="grid min-w-0 gap-0.5">
                    <span class="text-sm font-semibold text-n-slate-12">
                      {{ option.title }}
                    </span>
                    <span class="text-xs leading-4 text-n-slate-11">
                      {{ option.description }}
                    </span>
                  </span>
                  <i
                    v-if="currentType === option.value"
                    class="i-hugeicons-checkmark-circle-02 ml-auto size-4 shrink-0 text-n-brand"
                    aria-hidden="true"
                  />
                </label>
              </div>
              <p
                v-if="isEditing"
                class="flex items-start gap-2 m-0 text-xs text-n-slate-11"
              >
                <i
                  class="i-hugeicons-information-circle mt-px size-3.5 shrink-0"
                  aria-hidden="true"
                />
                {{
                  $t('INBOX_MGMT.WHATSAPP_TEMPLATES.TYPE_NOTICES.EDIT_LOCKED')
                }}
              </p>
            </div>

            <div
              v-if="typeNotice"
              class="flex items-start gap-3 p-3 rounded-xl bg-n-blue-2 outline outline-1 outline-n-blue-5"
              data-testid="template-type-notice"
            >
              <i
                class="i-hugeicons-information-circle mt-0.5 size-4 shrink-0 text-n-blue-11"
                aria-hidden="true"
              />
              <div class="grid min-w-0 gap-1 text-xs leading-5 text-n-slate-11">
                <span>{{ typeNotice }}</span>
                <span v-if="isCatalogType || isOrderDetailsType">
                  {{
                    $t(
                      'INBOX_MGMT.WHATSAPP_TEMPLATES.TYPE_NOTICES.NOT_SENDABLE'
                    )
                  }}
                </span>
                <router-link
                  v-if="isCatalogType && catalogsRoute"
                  :to="catalogsRoute"
                  class="inline-flex items-center gap-1 font-medium text-n-brand hover:underline"
                  data-testid="template-catalogs-link"
                >
                  <i class="i-hugeicons-store-01 size-3.5" aria-hidden="true" />
                  {{
                    $t(
                      'INBOX_MGMT.WHATSAPP_TEMPLATES.TYPE_NOTICES.CATALOG_LINK'
                    )
                  }}
                </router-link>
              </div>
            </div>
          </section>

          <section
            v-show="editorSection === 'CONTENT'"
            class="whatsapp-template-section"
          >
            <div class="whatsapp-template-section-heading">
              <span class="whatsapp-template-section-icon">
                <i class="i-lucide-text-cursor-input size-4" />
              </span>
              <div>
                <h3>
                  {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.SECTIONS.CONTENT') }}
                </h3>
                <p>
                  {{
                    $t('INBOX_MGMT.WHATSAPP_TEMPLATES.SECTIONS.CONTENT_HINT')
                  }}
                </p>
              </div>
            </div>
            <div v-if="isAuthentication" class="space-y-4">
              <p class="text-sm text-n-slate-11">
                {{
                  $t('INBOX_MGMT.WHATSAPP_TEMPLATES.AUTHENTICATION.DESCRIPTION')
                }}
              </p>
              <label class="flex items-start gap-2 text-sm text-n-slate-12">
                <input
                  v-model="form.add_security_recommendation"
                  type="checkbox"
                  class="!m-0 accent-n-brand"
                />
                {{
                  $t('INBOX_MGMT.WHATSAPP_TEMPLATES.AUTHENTICATION.SECURITY')
                }}
              </label>
              <NextInput
                v-model="form.code_expiration_minutes"
                type="number"
                min="1"
                max="90"
                :label="
                  $t('INBOX_MGMT.WHATSAPP_TEMPLATES.AUTHENTICATION.EXPIRY')
                "
                :message="
                  $t('INBOX_MGMT.WHATSAPP_TEMPLATES.AUTHENTICATION.EXPIRY_HINT')
                "
              />
            </div>
            <template v-else>
              <label class="whatsapp-template-field">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FIELDS.VARIABLE_TYPE') }}
                </span>
                <NextSelect
                  v-model="form.parameter_format"
                  :disabled="isEditing"
                  :options="[
                    {
                      value: 'POSITIONAL',
                      label: $t(
                        'INBOX_MGMT.WHATSAPP_TEMPLATES.VARIABLE_TYPES.POSITIONAL'
                      ),
                    },
                    {
                      value: 'NAMED',
                      label: $t(
                        'INBOX_MGMT.WHATSAPP_TEMPLATES.VARIABLE_TYPES.NAMED'
                      ),
                    },
                  ]"
                  class="w-full [&>select]:w-full"
                />
              </label>

              <p
                v-if="!allowsHeader"
                class="flex items-start gap-2 m-0 text-xs text-n-slate-11"
                data-testid="template-header-not-allowed"
              >
                <i
                  class="i-hugeicons-information-circle mt-px size-3.5 shrink-0"
                  aria-hidden="true"
                />
                {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.HEADER_NOT_ALLOWED') }}
              </p>
              <div
                v-else
                class="whatsapp-template-two-columns whatsapp-template-header-grid"
              >
                <label class="whatsapp-template-field">
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FIELDS.HEADER') }}
                  </span>
                  <NextSelect
                    v-model="form.header_format"
                    :options="headerOptions"
                    class="w-full [&>select]:w-full"
                  />
                </label>

                <label
                  v-if="form.header_format === 'TEXT'"
                  class="whatsapp-template-field"
                >
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FIELDS.HEADER_TEXT') }}
                  </span>
                  <NextInput
                    v-model="form.header_text"
                    type="text"
                    class="mt-1 !mb-0"
                    :message-type="isHeaderValid ? 'info' : 'error'"
                    :maxlength="headerMaxLength"
                    :aria-invalid="!isHeaderValid"
                  />
                  <span
                    class="flex items-center justify-between gap-2 text-xs"
                    :class="
                      isHeaderValid ? 'text-n-slate-10' : 'text-n-ruby-11'
                    "
                  >
                    <span>
                      {{
                        isHeaderValid
                          ? $t(
                              'INBOX_MGMT.WHATSAPP_TEMPLATES.HEADER_LIMIT_HINT',
                              { max: headerMaxLength }
                            )
                          : $t(
                              'INBOX_MGMT.WHATSAPP_TEMPLATES.HEADER_LIMIT_ERROR',
                              { max: headerMaxLength }
                            )
                      }}
                    </span>
                    <span class="shrink-0 tabular-nums">
                      {{
                        $t('INBOX_MGMT.WHATSAPP_TEMPLATES.CHARACTER_COUNT', {
                          current: headerLength,
                          max: headerMaxLength,
                        })
                      }}
                    </span>
                  </span>
                </label>
                <div v-if="mediaConfig" class="whatsapp-template-field">
                  <span class="text-sm font-medium text-n-slate-12">{{
                    $t('INBOX_MGMT.WHATSAPP_TEMPLATES.MEDIA.LABEL')
                  }}</span>
                  <input
                    ref="headerMediaFile"
                    type="file"
                    class="hidden"
                    :accept="mediaConfig.accept"
                    :disabled="mediaUploading || isSaving"
                    @change="uploadHeaderMedia"
                  />
                  <NextButton
                    type="button"
                    icon="i-lucide-upload"
                    :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.MEDIA.UPLOAD')"
                    :is-loading="mediaUploading"
                    :disabled="mediaUploading || isSaving"
                    faded
                    @click="$refs.headerMediaFile?.click()"
                  />
                  <span class="text-xs text-n-slate-11">{{
                    mediaConfig.hint
                  }}</span>
                  <span class="text-xs text-n-slate-11">{{
                    $t('INBOX_MGMT.WHATSAPP_TEMPLATES.MEDIA.HINT')
                  }}</span>
                  <span
                    v-if="mediaFilename"
                    role="status"
                    class="break-all text-xs text-n-teal-11"
                  >
                    {{
                      $t('INBOX_MGMT.WHATSAPP_TEMPLATES.MEDIA.READY', {
                        filename: mediaFilename,
                      })
                    }}
                  </span>
                  <span
                    v-if="mediaError"
                    role="alert"
                    class="text-xs text-n-ruby-11"
                  >
                    {{ mediaError }}
                  </span>
                </div>
              </div>

              <label ref="bodyField" class="whatsapp-template-field">
                <span class="flex items-center justify-between gap-2">
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FIELDS.BODY') }}
                  </span>
                  <SystemParameterMenu
                    v-if="form.parameter_format === 'NAMED'"
                    format="named"
                    @insert="insertBodyParameter"
                  />
                </span>
                <NextTextArea
                  v-model="form.body"
                  min-height="8rem"
                  resize
                  class="mt-1 !mb-0"
                  :message-type="isBodyValid ? 'info' : 'error'"
                  :max-length="bodyMaxLength"
                  :aria-invalid="!isBodyValid"
                  :placeholder="bodyPlaceholder"
                />
                <span
                  class="flex items-start justify-between gap-3 text-xs mt-1"
                  :class="isBodyValid ? 'text-n-slate-10' : 'text-n-ruby-11'"
                >
                  <span>
                    {{ variableHint }}
                    {{
                      $t('INBOX_MGMT.WHATSAPP_TEMPLATES.BODY_LIMIT_HINT', {
                        max: bodyMaxLength,
                      })
                    }}
                  </span>
                  <span class="shrink-0 tabular-nums">
                    {{
                      $t('INBOX_MGMT.WHATSAPP_TEMPLATES.CHARACTER_COUNT', {
                        current: bodyLength,
                        max: bodyMaxLength,
                      })
                    }}
                  </span>
                </span>
              </label>

              <label class="whatsapp-template-field">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FIELDS.FOOTER') }}
                </span>
                <NextInput
                  v-model="form.footer"
                  type="text"
                  class="mt-1 !mb-0"
                  :message-type="isFooterValid ? 'info' : 'error'"
                  :maxlength="footerMaxLength"
                  :aria-invalid="!isFooterValid"
                />
                <span
                  class="flex items-center justify-between gap-2 text-xs"
                  :class="isFooterValid ? 'text-n-slate-10' : 'text-n-ruby-11'"
                >
                  <span>
                    {{
                      isFooterValid
                        ? $t(
                            'INBOX_MGMT.WHATSAPP_TEMPLATES.FOOTER_LIMIT_HINT',
                            {
                              max: footerMaxLength,
                            }
                          )
                        : $t(
                            'INBOX_MGMT.WHATSAPP_TEMPLATES.FOOTER_LIMIT_ERROR',
                            {
                              max: footerMaxLength,
                            }
                          )
                    }}
                  </span>
                  <span class="shrink-0 tabular-nums">
                    {{
                      $t('INBOX_MGMT.WHATSAPP_TEMPLATES.CHARACTER_COUNT', {
                        current: footerLength,
                        max: footerMaxLength,
                      })
                    }}
                  </span>
                </span>
              </label>
            </template>
          </section>

          <section
            v-show="editorSection === 'ACTIONS'"
            class="whatsapp-template-section"
          >
            <div class="whatsapp-template-section-heading">
              <span class="whatsapp-template-section-icon">
                <i class="i-lucide-mouse-pointer-click size-4" />
              </span>
              <div>
                <h3>
                  {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.SECTIONS.ACTIONS') }}
                </h3>
                <p v-if="isStandardType">
                  {{
                    $t('INBOX_MGMT.WHATSAPP_TEMPLATES.SECTIONS.ACTIONS_HINT')
                  }}
                </p>
                <p v-else>
                  {{
                    $t(
                      'INBOX_MGMT.WHATSAPP_TEMPLATES.SECTIONS.ACTIONS_FIXED_HINT'
                    )
                  }}
                </p>
              </div>
            </div>
            <div
              v-if="isAuthentication"
              class="rounded-xl border border-n-weak p-4 space-y-2"
            >
              <p class="text-sm font-semibold text-n-slate-12">
                {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.AUTHENTICATION.COPY') }}
              </p>
              <p class="text-sm text-n-slate-11">
                {{
                  $t('INBOX_MGMT.WHATSAPP_TEMPLATES.AUTHENTICATION.BUTTON_HINT')
                }}
              </p>
            </div>
            <div
              v-else-if="isCatalogType"
              class="rounded-xl border border-n-weak p-4 space-y-2"
              data-testid="template-catalog-button"
            >
              <p
                class="flex items-center gap-2 m-0 text-sm font-semibold text-n-slate-12"
              >
                <i class="i-hugeicons-store-01 size-4" aria-hidden="true" />
                {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.PREVIEW.CATALOG_BUTTON') }}
              </p>
              <p class="m-0 text-sm text-n-slate-11">
                {{
                  $t(
                    'INBOX_MGMT.WHATSAPP_TEMPLATES.TYPE_ACTIONS.CATALOG_BUTTON'
                  )
                }}
              </p>
            </div>
            <div v-else-if="isOrderDetailsType" class="whatsapp-template-field">
              <span class="text-sm font-medium text-n-slate-12">
                {{
                  $t(
                    'INBOX_MGMT.WHATSAPP_TEMPLATES.FIELDS.ORDER_DETAILS_BUTTON'
                  )
                }}
              </span>
              <NextInput
                v-model="form.order_details_button_text"
                type="text"
                class="mt-1 !mb-0"
                :message-type="isOrderButtonValid ? 'info' : 'error'"
                :maxlength="orderButtonMaxLength"
                :aria-invalid="!isOrderButtonValid"
              />
              <span
                class="flex items-start justify-between gap-2 text-xs"
                :class="
                  isOrderButtonValid ? 'text-n-slate-10' : 'text-n-ruby-11'
                "
              >
                <span>
                  {{
                    isOrderButtonValid
                      ? $t(
                          'INBOX_MGMT.WHATSAPP_TEMPLATES.TYPE_ACTIONS.ORDER_DETAILS_BUTTON',
                          { max: orderButtonMaxLength }
                        )
                      : $t(
                          'INBOX_MGMT.WHATSAPP_TEMPLATES.ORDER_BUTTON_LIMIT_ERROR',
                          { max: orderButtonMaxLength }
                        )
                  }}
                </span>
                <span class="shrink-0 tabular-nums">
                  {{
                    $t('INBOX_MGMT.WHATSAPP_TEMPLATES.CHARACTER_COUNT', {
                      current: orderButtonLength,
                      max: orderButtonMaxLength,
                    })
                  }}
                </span>
              </span>
            </div>
            <div
              v-else-if="!isStandardType"
              class="rounded-xl border border-n-weak p-4 space-y-2"
              data-testid="template-no-buttons"
            >
              <p
                class="flex items-center gap-2 m-0 text-sm font-semibold text-n-slate-12"
              >
                <i
                  class="size-4"
                  :class="
                    isCallPermissionType
                      ? 'i-hugeicons-call-add'
                      : 'i-hugeicons-package-delivered'
                  "
                  aria-hidden="true"
                />
                {{
                  isCallPermissionType
                    ? $t(
                        'INBOX_MGMT.WHATSAPP_TEMPLATES.PREVIEW.CALL_PERMISSION_TITLE'
                      )
                    : $t(
                        'INBOX_MGMT.WHATSAPP_TEMPLATES.TYPES.ORDER_STATUS.TITLE'
                      )
                }}
              </p>
              <p class="m-0 text-sm text-n-slate-11">
                {{
                  isCallPermissionType
                    ? $t(
                        'INBOX_MGMT.WHATSAPP_TEMPLATES.TYPE_ACTIONS.CALL_PERMISSION_REQUEST'
                      )
                    : $t(
                        'INBOX_MGMT.WHATSAPP_TEMPLATES.TYPE_ACTIONS.NO_BUTTONS'
                      )
                }}
              </p>
            </div>
            <template v-else>
              <TemplateButtonsField
                v-model="form.buttons"
                :inbox-id="inbox.id"
              />
            </template>
            <MessageValidityField
              v-model="form.message_send_ttl_seconds"
              :category="form.category"
            />
          </section>
        </div>

        <aside class="whatsapp-template-preview">
          <h3 class="mb-4 text-sm font-semibold text-n-slate-12">
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.PREVIEW_TITLE') }}
          </h3>
          <div class="whatsapp-template-preview-phone">
            <div class="whatsapp-template-preview-row">
              <div class="whatsapp-template-preview-bubble">
                <img
                  v-if="
                    mediaPreviewUrl &&
                    form.header_format === 'IMAGE' &&
                    !isAuthentication
                  "
                  :src="mediaPreviewUrl"
                  :alt="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.MEDIA.LABEL')"
                  class="mb-3 max-h-64 w-full rounded-lg object-contain"
                  data-testid="template-image-preview"
                />
                <div
                  v-if="previewHeader"
                  class="whatsapp-template-preview-header"
                >
                  {{ previewHeader }}
                </div>
                <div class="whatsapp-template-preview-text">
                  {{
                    previewBody ||
                    $t('INBOX_MGMT.WHATSAPP_TEMPLATES.PREVIEW_EMPTY')
                  }}
                </div>
                <div
                  v-if="previewFooter"
                  class="whatsapp-template-preview-footer"
                >
                  {{ previewFooter }}
                </div>
                <div
                  v-if="isCallPermissionType"
                  class="whatsapp-template-preview-permission"
                  data-testid="template-call-permission-preview"
                >
                  <i class="i-hugeicons-call-add size-5 shrink-0" />
                  <div class="min-w-0">
                    <strong>
                      {{
                        $t(
                          'INBOX_MGMT.WHATSAPP_TEMPLATES.PREVIEW.CALL_PERMISSION_TITLE'
                        )
                      }}
                    </strong>
                    <span>
                      {{
                        $t(
                          'INBOX_MGMT.WHATSAPP_TEMPLATES.PREVIEW.CALL_PERMISSION_TEXT'
                        )
                      }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="visibleButtons.length" class="mt-3 space-y-2">
              <div
                v-for="button in visibleButtons"
                :key="`${button.type}-${button.text}`"
                class="whatsapp-template-preview-button"
              >
                <i
                  v-if="button.icon"
                  class="size-4 shrink-0"
                  :class="button.icon"
                  aria-hidden="true"
                />
                {{ button.text }}
              </div>
            </div>
          </div>
        </aside>
      </div>

      <div class="whatsapp-template-modal-footer">
        <NextButton
          ghost
          type="button"
          :disabled="isSaving"
          :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.CANCEL')"
          @click="closeTemplateModal"
        />
        <NextButton
          v-if="currentStepIndex > 0"
          outline
          type="button"
          data-testid="template-step-back"
          :disabled="isSaving"
          :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.BACK')"
          @click="previousEditorSection"
        />
        <NextButton
          v-if="!isLastStep"
          type="button"
          data-testid="template-step-next"
          :disabled="!canAdvance"
          :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.NEXT')"
          @click="nextEditorSection"
        />
        <NextButton
          v-else
          type="button"
          data-testid="template-step-submit"
          :disabled="!canSave || isSaving"
          :is-loading="isSaving"
          :label="
            isEditing
              ? $t('INBOX_MGMT.WHATSAPP_TEMPLATES.UPDATE')
              : $t('INBOX_MGMT.WHATSAPP_TEMPLATES.CREATE')
          "
          @click="saveTemplate"
        />
      </div>
    </woot-modal>

    <woot-modal
      v-model:show="showDeleteConfirmModal"
      :on-close="closeDeleteConfirmModal"
    >
      <woot-modal-header
        :header-title="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE')"
        :header-content="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE_CONFIRM')"
      />
      <div class="flex items-center justify-end gap-2 px-8 py-6">
        <NextButton
          ghost
          :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.CANCEL')"
          @click="closeDeleteConfirmModal"
        />
        <NextButton
          ruby
          :is-loading="!!deletingTemplateId"
          :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE')"
          @click="deleteTemplate"
        />
      </div>
    </woot-modal>

    <woot-modal v-model:show="showFeedbackModal" :on-close="closeFeedbackModal">
      <woot-modal-header
        :header-title="feedbackTitle"
        :header-content="feedbackMessage"
      />
      <div class="flex items-center justify-end px-8 py-6">
        <NextButton
          :ruby="feedbackTone === 'error'"
          :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.UNDERSTOOD')"
          @click="closeFeedbackModal"
        />
      </div>
    </woot-modal>
  </div>
</template>

<style scoped>
:deep(.whatsapp-template-modal-shell) {
  background-color: rgb(var(--solid-1)) !important;
  border: 1px solid rgb(var(--slate-5)) !important;
  border-radius: 24px !important;
  box-shadow: 0 24px 80px rgb(0 0 0 / 42%) !important;
  max-height: calc(100dvh - 32px) !important;
  overflow: hidden !important;
  width: min(1180px, calc(100vw - 48px)) !important;
}

.whatsapp-template-steps {
  display: flex;
  gap: 8px;
  padding: 12px 24px;
  border-bottom: 1px solid rgb(var(--slate-4));
  overflow-x: auto;
}
.whatsapp-template-steps button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-radius: 10px;
  padding: 10px 12px;
  white-space: nowrap;
  font-size: 13px;
  color: rgb(var(--slate-11));
}
.whatsapp-template-steps button.is-active {
  background: rgb(var(--blue-3));
  color: rgb(var(--blue-11));
}
.whatsapp-template-steps button span {
  display: grid;
  place-items: center;
  border: 1px solid currentColor;
  border-radius: 50%;
  width: 22px;
  height: 22px;
  font-size: 11px;
}
.whatsapp-template-modal-header {
  align-items: center;
  background-color: rgb(var(--solid-2));
  border-bottom: 1px solid rgb(var(--slate-4));
  display: flex;
  gap: 12px;
  min-height: 56px;
  padding: 18px 24px;
}

.whatsapp-template-modal-icon,
.whatsapp-template-section-icon {
  align-items: center;
  background-color: rgb(var(--blue-4));
  border: 0;
  border-radius: 10px;
  color: rgb(var(--blue-11));
  display: flex;
  flex: 0 0 auto;
  height: 36px;
  justify-content: center;
  width: 36px;
}

.whatsapp-template-modal-body {
  align-items: start;
  display: grid;
  gap: 20px;
  grid-template-columns: minmax(0, 1.35fr) minmax(300px, 0.8fr);
  max-height: calc(100dvh - 290px);
  min-width: 0;
  overflow: auto;
  padding: 20px 24px;
  width: 100%;
}

.whatsapp-template-form {
  align-content: start;
  align-self: start;
  display: grid;
  gap: 14px;
  min-width: 0;
  order: 1;
  width: 100%;
}

.whatsapp-template-section {
  align-content: start;
  background: rgb(var(--solid-1));
  border: 1px solid rgb(var(--slate-4));
  border-radius: 12px;
  display: grid;
  gap: 14px;
  min-width: 0;
  padding: 18px;
  width: 100%;
}

.whatsapp-template-section-heading {
  align-items: flex-start;
  display: flex;
  gap: 10px;
  padding-bottom: 2px;
}

.whatsapp-template-section-heading h3 {
  color: rgb(var(--slate-12));
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.25rem;
  margin: 0 0 2px;
}

.whatsapp-template-section-heading p {
  color: rgb(var(--slate-10));
  font-size: 0.75rem;
  line-height: 1.125rem;
  margin: 0;
}

.whatsapp-template-section-icon {
  border-radius: 8px;
  height: 30px;
  width: 30px;
}

.whatsapp-template-two-columns {
  align-items: start;
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  min-width: 0;
  width: 100%;
}

.whatsapp-template-header-grid {
  grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
}

.whatsapp-template-field {
  align-self: start;
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0;
  min-width: 0;
  width: 100%;
}

.whatsapp-template-field > :deep(.mt-1) {
  margin-top: 0 !important;
}

.whatsapp-template-variable-type {
  max-width: 100% !important;
}

.whatsapp-template-preview {
  align-self: start;
  min-width: 0;
  order: 2;
  position: sticky;
  top: 0;
  width: 100%;
}

.whatsapp-template-preview > h3 {
  align-items: center;
  display: flex;
  gap: 8px;
}

.whatsapp-template-preview > h3::before {
  background-color: rgb(var(--teal-9));
  border-radius: 999px;
  content: '';
  height: 8px;
  width: 8px;
}

.whatsapp-template-preview-phone {
  background: radial-gradient(
      circle at 16px 16px,
      rgb(17 94 89 / 10%) 0 1px,
      transparent 1px
    ),
    linear-gradient(180deg, #e7ddd0, #d8ccbd);
  background-size:
    18px 18px,
    auto;
  border: 1px solid rgb(var(--slate-5));
  border-radius: 12px;
  min-height: 320px;
  overflow: hidden;
  padding: 22px;
}

:global(.dark) .whatsapp-template-preview-phone {
  background: radial-gradient(
      circle at 16px 16px,
      rgb(255 255 255 / 5%) 0 1px,
      transparent 1px
    ),
    linear-gradient(180deg, #17211f, #111817);
}

.whatsapp-template-preview-row {
  display: flex;
  justify-content: flex-end;
}

.whatsapp-template-preview-bubble {
  background-color: #dcf8c6;
  border-radius: 10px 10px 2px 10px;
  box-shadow: 0 1px 2px rgb(17 24 39 / 22%);
  color: #111b21;
  max-width: min(100%, 25rem);
  padding: 12px 14px;
}

.whatsapp-template-preview-header {
  color: #111b21;
  font-size: 0.875rem;
  font-weight: 700;
  line-height: 1.25rem;
  margin-bottom: 0.5rem;
  overflow-wrap: anywhere;
}

.whatsapp-template-preview-text {
  color: #111b21;
  font-size: 0.875rem;
  line-height: 1.35rem;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}

.whatsapp-template-preview-footer {
  border-top: 1px solid rgb(17 24 39 / 10%);
  color: #667781;
  font-size: 0.75rem;
  margin-top: 0.75rem;
  overflow-wrap: anywhere;
  padding-top: 0.5rem;
}

:global(.dark) .whatsapp-template-preview-bubble {
  background-color: #005c4b;
  color: #f4fbf8;
}

:global(.dark) .whatsapp-template-preview-header,
:global(.dark) .whatsapp-template-preview-text {
  color: #f4fbf8;
}

:global(.dark) .whatsapp-template-preview-footer {
  border-top-color: rgb(255 255 255 / 12%);
  color: #bfd8d3;
}

.whatsapp-template-preview-permission {
  align-items: flex-start;
  border-top: 1px solid rgb(17 24 39 / 10%);
  color: #0b7a75;
  display: flex;
  gap: 10px;
  margin-top: 0.75rem;
  padding-top: 0.625rem;
}

.whatsapp-template-preview-permission strong {
  color: #111b21;
  display: block;
  font-size: 0.8125rem;
}

.whatsapp-template-preview-permission span {
  color: #667781;
  display: block;
  font-size: 0.75rem;
  line-height: 1.1rem;
}

:global(.dark) .whatsapp-template-preview-permission {
  border-top-color: rgb(255 255 255 / 12%);
  color: #68d4ca;
}

:global(.dark) .whatsapp-template-preview-permission strong {
  color: #f4fbf8;
}

:global(.dark) .whatsapp-template-preview-permission span {
  color: #bfd8d3;
}

.whatsapp-template-preview-button {
  align-items: center;
  display: flex;
  gap: 6px;
  justify-content: center;
  background-color: rgb(255 255 255 / 86%);
  border: 1px solid rgb(17 24 39 / 8%);
  border-radius: 10px;
  color: #0b7a75;
  font-weight: 600;
  padding: 9px 12px;
  text-align: center;
}

:global(.dark) .whatsapp-template-preview-button {
  background-color: #1f2c2b;
  border-color: rgb(255 255 255 / 7%);
  color: #68d4ca;
}

.whatsapp-template-modal-footer {
  align-items: center;
  background-color: rgb(var(--solid-1));
  border-top: 1px solid rgb(var(--slate-4));
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  min-height: 72px;
  justify-content: flex-end;
  padding: 16px 24px;
}

@media (max-width: 960px) {
  :deep(.whatsapp-template-modal-shell) {
    max-height: calc(100vh - 24px) !important;
    width: calc(100vw - 24px) !important;
  }

  .whatsapp-template-modal-body {
    grid-template-columns: 1fr;
    max-height: calc(100dvh - 270px);
    padding: 16px;
  }

  .whatsapp-template-preview {
    order: 2;
    position: static;
  }

  .whatsapp-template-preview-phone {
    min-height: 240px;
  }

  .whatsapp-template-form {
    order: 1;
  }

  .whatsapp-template-two-columns,
  .whatsapp-template-header-grid {
    grid-template-columns: 1fr;
  }

  .whatsapp-template-modal-header,
  .whatsapp-template-modal-footer {
    padding-left: 16px;
    padding-right: 16px;
  }
}
</style>
